"use client";

import { ArrowRight, Facebook, Instagram, Linkedin, Mail, Youtube } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const ROUTES = {
  home: "/",
  courseFinder: "/course-finder/",
  courseIntelligence: "/course-intelligence/",
  destinations: "/#home-destinations",
  golfGuides: "/blog/",
  cityDiscovery: "/#home-city-discovery",
  golfPassport: "/#home-golf-passport",
  joinCommunity: "/#home-community-cta",
  partnerWithUs: "/teelistclub-landing/",
  partnerEnquiry: "/teelistclub-landing/#partner-enquiry",
  privacy: "/privacy-policy/",
  terms: "/terms-of-use/"
};
const footerGroups = [{
  category: "Explore",
  links: [{
    label: "Course Finder",
    path: ROUTES.courseFinder
  }, {
    label: "Course Intelligence",
    path: ROUTES.courseIntelligence
  }, {
    label: "Destinations",
    path: ROUTES.destinations
  }, {
    label: "Golf Travel Guides",
    path: ROUTES.golfGuides
  }]
}, {
  category: "TeeListClub",
  links: [{
    label: "Golf Passport",
    path: ROUTES.golfPassport
  }, {
    label: "City Discovery",
    path: ROUTES.cityDiscovery
  }, {
    label: "Join TeeListClub",
    path: ROUTES.joinCommunity
  }, {
    label: "Homepage",
    path: ROUTES.home
  }]
}, {
  category: "For Golf Courses",
  links: [{
    label: "Partner With Us",
    path: ROUTES.partnerWithUs
  }, {
    label: "Submit or Verify Your Course",
    path: ROUTES.partnerEnquiry
  }, {
    label: "Partner Enquiries",
    path: "mailto:hello@teelistclub.com",
    external: true
  }]
}];
const socialLinks = [{
  icon: Instagram,
  href: "",
  label: "Instagram"
}, {
  icon: Facebook,
  href: "",
  label: "Facebook"
}, {
  icon: Youtube,
  href: "",
  label: "YouTube"
}, {
  icon: Linkedin,
  href: "",
  label: "LinkedIn"
}].filter(social => social.href.trim() !== "");
export default function Footer() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.08
  });
  const currentYear = new Date().getFullYear();
  return <footer id="footer" data-section-id="261128" className={["relative w-full overflow-hidden", "bg-[#073F31]", "text-[#FFFDF7]"].join(" ")} style={{
    fontFamily: '"Raleway", Arial, sans-serif'
  }}>
      {/* Restrained background depth */}
      <div aria-hidden="true" className={["pointer-events-none absolute inset-0", "bg-[radial-gradient(circle_at_12%_18%,rgba(191,255,0,0.045),transparent_32%),radial-gradient(circle_at_88%_100%,rgba(216,189,103,0.055),transparent_30%)]"].join(" ")} />

      {/* Main footer */}
      <div ref={ref} className={["relative z-10", "mx-auto w-full max-w-[1480px]", "px-5 py-12", "md:px-8 md:py-14", "lg:px-12 lg:py-16", "transition-all duration-700", isVisible ? "translate-y-0 opacity-100" : "translate-y-5 opacity-0"].join(" ")}>
        <div className={["grid grid-cols-1", "gap-10", "sm:grid-cols-2", "lg:grid-cols-[1.4fr_0.75fr_0.85fr_1fr]", "lg:gap-0"].join(" ")}>
          {/* Brand */}
          <div className="sm:col-span-2 lg:col-span-1 lg:pr-12">
            <Link to={ROUTES.home} aria-label="TeeListClub homepage" className="inline-block">
              <WvcLogo className={["h-auto w-[165px]", "brightness-0 invert", "opacity-95", "[&_svg]:h-auto", "[&_svg]:w-full", "[&_svg]:brightness-0", "[&_svg]:invert"].join(" ")} />
            </Link>

            <p className={["mt-5 max-w-[350px]", "text-[14px] font-medium", "leading-[1.65]", "text-[#FFFDF7]/72"].join(" ")}>
              A premium golf discovery and course-intelligence platform for
              travellers across Asia.
            </p>

            <a href="mailto:hello@teelistclub.com" className={["mt-5 inline-flex items-center gap-2.5", "text-[13px] font-semibold", "text-[#D8BD67]", "transition-colors duration-200", "hover:text-[#BFFF00]"].join(" ")}>
              <Mail className="size-4" strokeWidth={1.6} aria-hidden="true" />

              hello@teelistclub.com
            </a>

            {socialLinks.length > 0 && <div className="mt-5 flex items-center gap-2.5">
                {socialLinks.map(social => {
              const Icon = social.icon;
              return <a key={social.label} href={social.href} target="_blank" rel="noreferrer" aria-label={social.label} className={["flex size-9", "items-center justify-center", "rounded-full", "border border-[#FFFDF7]/20", "text-[#FFFDF7]/80", "transition-all duration-200", "hover:border-[#BFFF00]", "hover:bg-[#BFFF00]", "hover:text-[#073F31]"].join(" ")}>
                      <Icon className="size-[16px]" strokeWidth={1.7} aria-hidden="true" />
                    </a>;
            })}
              </div>}
          </div>

          {/* Footer navigation */}
          {footerGroups.map(group => <nav key={group.category} aria-label={group.category} className={["border-t border-[#FFFDF7]/12", "pt-7", "lg:border-l lg:border-t-0", "lg:px-8 lg:pt-1"].join(" ")}>
              <h2 className={["text-[10px] font-bold uppercase", "tracking-[0.19em]", "text-[#D8BD67]"].join(" ")}>
                {group.category}
              </h2>

              <ul className="mt-5 space-y-3">
                {group.links.map(item => <li key={item.label}>
                    {"external" in item && item.external ? <a href={item.path} className={["group inline-flex items-center", "text-[13px] font-medium", "leading-[1.45]", "text-[#FFFDF7]/68", "transition-colors duration-200", "hover:text-[#BFFF00]"].join(" ")}>
                        <ArrowRight className={["mr-0 size-0", "text-[#BFFF00]", "opacity-0", "transition-all duration-200", "group-hover:mr-2", "group-hover:size-3", "group-hover:opacity-100"].join(" ")} aria-hidden="true" />

                        {item.label}
                      </a> : <Link to={item.path} className={["group inline-flex items-center", "text-[13px] font-medium", "leading-[1.45]", "text-[#FFFDF7]/68", "transition-colors duration-200", "hover:text-[#BFFF00]"].join(" ")}>
                        <ArrowRight className={["mr-0 size-0", "text-[#BFFF00]", "opacity-0", "transition-all duration-200", "group-hover:mr-2", "group-hover:size-3", "group-hover:opacity-100"].join(" ")} aria-hidden="true" />

                        {item.label}
                      </Link>}
                  </li>)}
              </ul>
            </nav>)}
        </div>
      </div>

      {/* Bottom legal bar */}
      <div className={["relative z-10", "border-t border-[#FFFDF7]/12", "bg-[#032F25]/38"].join(" ")}>
        <div className={["mx-auto flex w-full max-w-[1480px]", "flex-col gap-3", "px-5 py-5", "text-[10px] font-medium", "text-[#FFFDF7]/52", "md:flex-row md:items-center md:justify-between", "md:px-8", "lg:px-12"].join(" ")}>
          <p>© {currentYear} TeeListClub. All rights reserved.</p>

          <div className={["flex flex-wrap", "gap-x-7 gap-y-2", "uppercase tracking-[0.07em]"].join(" ")}>
            <Link to={ROUTES.privacy} className="transition-colors duration-200 hover:text-[#BFFF00]">
              Privacy Policy
            </Link>

            <Link to={ROUTES.terms} className="transition-colors duration-200 hover:text-[#BFFF00]">
              Terms of Use
            </Link>
          </div>
        </div>
      </div>
    </footer>;
}